import { api } from "../actions/api";
import convertToMp3 from "./convertToMp3";

/**
 * Upload a file to S3 via the backend API
 * @param {File|Blob} file - The file or blob to upload
 * @param {string} fileName - Custom filename (optional)
 * @returns {Promise<Object>} - Upload response with file URLs
 */
export const uploadFile = async (file, fileName = null) => {
    try {
        const formData = new FormData();

        // If it's a blob, convert it to a File object with a proper name
        if (file instanceof Blob && !(file instanceof File)) {
            const fileExtension = file.type.split('/')[1] || 'png';
            const defaultFileName = fileName || `level-up-screenshot.${fileExtension}`;
            file = new File([file], defaultFileName, { type: file.type });
        }



        // Handle different file types like in Chat.jsx
        // Ensure audio files are converted to mp3
        if (file.type.startsWith("audio") && file.type !== "audio/mpeg") {
            try {
                file = await convertToMp3(file);
            } catch (err) {
                console.warn("convertToMp3 failed in uploadFile, using original file", err);
            }
        }

        if (file.type.startsWith("video")) {
            // For videos, we might need chunking (from Chat.jsx logic)
            // For now, let's handle images which are more common for level-up screenshots
            formData.append("media", file);
        } else if (file.type.startsWith("audio")) {
            formData.append("audio", file);
        } else {
            // Images and other files
            formData.append("media", file);
        }

        const response = await api.post("/user/chat/media/upload", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        if (response.status !== 200) {
            throw new Error("Upload failed");
        }

        const fileData = response.data;


        return fileData;

    } catch (error) {
        console.error("❌ Error uploading file:", error);
        throw error;
    }
};

/**
 * Convert canvas to blob and upload to S3
 * @param {HTMLCanvasElement} canvas - The canvas element to upload
 * @param {string} fileName - Custom filename (optional)
 * @param {string} mimeType - MIME type for the blob (default: image/png)
 * @param {number} quality - Image quality (0-1, default: 0.9)
 * @returns {Promise<Object>} - Upload response with file URLs
 */
export const uploadCanvasAsImage = (canvas, fileName = null, mimeType = 'image/png', quality = 0.9) => {
    return new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
            try {
                const result = await uploadFile(blob, fileName);
                resolve(result);
            } catch (error) {
                reject(error);
            }
        }, mimeType, quality);
    });
};